import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ComponentCard from '../../components/common/ComponentCard';
import SearchableSelect, {
  type SearchableSelectOption,
} from '../../components/common/SearchableSelect';
import Label from '../../components/form/Label';
import Input from '../../components/form/input/InputField';
import {
  api,
  apiEndpoints,
  type ApiRequestOptions,
} from '../../utils/apiService';
import {
  type TableSortState,
  toOrderingParam,
  toggleSortState,
} from '../../utils/listOrdering';
import { normalizeImportPhone } from '../../utils/vipPhone';
import VipCsvImportModal from './VipCsvImportModal';

type VipScope = 'store' | 'group';
type VipSortKey = 'full_name' | 'phone';

const VIP_SORT_KEY_TO_FIELD: Record<VipSortKey, string> = {
  full_name: 'full_name',
  phone: 'phone',
};

interface VipContact {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  display_name: string;
  phone: string;
  normalized_phone: string;
  store: number | null;
  store_group: number | null;
  scope: VipScope;
}

interface PaginatedVipContacts {
  results: VipContact[];
  count: number;
  page: number;
  page_size: number;
  total_pages: number;
}

interface VipContactsTabProps {
  storeOptions: SearchableSelectOption[];
  storeGroupOptions: SearchableSelectOption[];
}

const VipContactsTab: React.FC<VipContactsTabProps> = ({
  storeOptions,
  storeGroupOptions,
}) => {
  const [scope, setScope] = useState<VipScope>('store');
  const [selectedStoreId, setSelectedStoreId] = useState<number | ''>('');
  const [selectedGroupId, setSelectedGroupId] = useState<number | ''>('');
  const [contacts, setContacts] = useState<VipContact[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [sort, setSort] = useState<TableSortState<VipSortKey>>({
    key: null,
    direction: 'asc',
  });
  const ordering = toOrderingParam(sort, VIP_SORT_KEY_TO_FIELD);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newFirst, setNewFirst] = useState('');
  const [newLast, setNewLast] = useState('');
  const [newFull, setNewFull] = useState('');
  const [newPhone, setNewPhone] = useState('');

  const ownerReady =
    scope === 'store' ? Boolean(selectedStoreId) : Boolean(selectedGroupId);

  const requestOptions: ApiRequestOptions = useMemo(() => {
    if (scope === 'group') {
      return { skipStoreHeader: true };
    }
    if (!selectedStoreId) {
      return { skipStoreHeader: true };
    }
    return { headers: { 'X-Store-ID': String(selectedStoreId) } };
  }, [scope, selectedStoreId]);

  const loadContacts = useCallback(async () => {
    if (!ownerReady) {
      setContacts([]);
      setCount(0);
      setTotalPages(1);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await api.get(
        apiEndpoints.getVipContacts({
          scope,
          store_group_id:
            scope === 'group' && selectedGroupId
              ? Number(selectedGroupId)
              : undefined,
          q: appliedSearch || undefined,
          page,
          ordering: ordering ?? undefined,
        }),
        requestOptions,
      );
      if (!response?.ok) {
        const body = await response?.json().catch(() => ({}));
        setError(body.detail || 'Failed to load VIP contacts.');
        setContacts([]);
        return;
      }
      const data = (await response.json()) as PaginatedVipContacts;
      setContacts(data.results || []);
      setCount(data.count || 0);
      setTotalPages(data.total_pages || 1);
    } catch {
      setError('Failed to load VIP contacts.');
    } finally {
      setLoading(false);
    }
  }, [
    appliedSearch,
    ordering,
    ownerReady,
    page,
    requestOptions,
    scope,
    selectedGroupId,
  ]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  const handleAdd = async () => {
    if (!ownerReady || !newPhone.trim()) {
      setError('Phone is required.');
      return;
    }
    const normalizedPhone = normalizeImportPhone(newPhone);
    if (!normalizedPhone) {
      setError(
        'Phone must be a US/Canada number: 10 digits, or 11 digits starting with 1.',
      );
      return;
    }
    setAdding(true);
    setError('');
    setSuccess('');
    try {
      const body: Record<string, unknown> = {
        scope,
        first_name: newFirst,
        last_name: newLast,
        full_name: newFull,
        phone: normalizedPhone,
      };
      if (scope === 'group') {
        body.store_group_id = selectedGroupId;
      }
      const response = await api.post(
        apiEndpoints.createVipContact(),
        body,
        requestOptions,
      );
      const payload = await response?.json().catch(() => ({}));
      if (!response?.ok) {
        if (response?.status === 409) {
          setError('A VIP contact with this phone already exists.');
        } else {
          setError(
            payload.phone?.[0] ||
              payload.detail ||
              'Could not add VIP contact.',
          );
        }
        return;
      }
      setNewFirst('');
      setNewLast('');
      setNewFull('');
      setNewPhone('');
      setSuccess('VIP contact added.');
      setPage(1);
      await loadContacts();
    } catch {
      setError('Could not add VIP contact.');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Remove this VIP contact?')) return;
    setError('');
    const response = await api.delete(
      apiEndpoints.deleteVipContact(id),
      requestOptions,
    );
    if (!response?.ok && response?.status !== 204) {
      setError('Could not delete VIP contact.');
      return;
    }
    await loadContacts();
  };

  return (
    <ComponentCard title="VIP Contacts" className="overflow-hidden">
      <div className="space-y-6">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          VIP flags are available after get store info returns. They do not
          change the first greeting. Calls, customers, reservations, and
          voicemail also show a VIP tag. Store-level contacts override the same
          phone on the store group. The spoken name is first name only. Phones
          are stored as +1 plus 10 US/Canada digits; spaces, parentheses, and
          dashes are stripped on save.
        </p>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <Label>Scope</Label>
            <div className="mt-2 flex gap-3 text-sm">
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  checked={scope === 'store'}
                  onChange={() => {
                    setScope('store');
                    setPage(1);
                  }}
                />
                Store
              </label>
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  checked={scope === 'group'}
                  onChange={() => {
                    setScope('group');
                    setPage(1);
                  }}
                />
                Store group
              </label>
            </div>
          </div>
          {scope === 'store' ? (
            <div>
              <Label>Store</Label>
              <SearchableSelect
                options={storeOptions}
                value={selectedStoreId}
                onChange={(val) => {
                  setSelectedStoreId(val === '' ? '' : parseInt(val, 10) || '');
                  setPage(1);
                }}
                placeholder="Select a store"
              />
            </div>
          ) : (
            <div>
              <Label>Store group</Label>
              <SearchableSelect
                options={storeGroupOptions}
                value={selectedGroupId}
                onChange={(val) => {
                  setSelectedGroupId(val === '' ? '' : parseInt(val, 10) || '');
                  setPage(1);
                }}
                placeholder="Select a store group"
              />
            </div>
          )}
          <div>
            <Label>Search</Label>
            <div className="flex gap-2">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Name or phone"
              />
              <button
                type="button"
                className="rounded-2xl bg-[rgb(var(--app-brand))] px-4 py-2 text-sm font-semibold text-white"
                onClick={() => {
                  setAppliedSearch(search.trim());
                  setPage(1);
                }}
              >
                Search
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="rounded-2xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-600">
            {error}
          </div>
        )}
        {success && (
          <div className="rounded-2xl border border-success-200 bg-success-50 px-4 py-3 text-sm text-success-700">
            {success}
          </div>
        )}

        {ownerReady && (
          <>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
              <Input
                value={newFirst}
                onChange={(e) => setNewFirst(e.target.value)}
                placeholder="First name"
              />
              <Input
                value={newLast}
                onChange={(e) => setNewLast(e.target.value)}
                placeholder="Last name"
              />
              <Input
                value={newFull}
                onChange={(e) => setNewFull(e.target.value)}
                placeholder="Full name"
              />
              <Input
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                placeholder="Phone (+1…)"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={adding}
                  onClick={handleAdd}
                  className="flex-1 rounded-2xl bg-[rgb(var(--app-brand))] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                >
                  {adding ? 'Adding…' : 'Add'}
                </button>
                <button
                  type="button"
                  onClick={() => setImportOpen(true)}
                  className="rounded-2xl border border-[rgb(var(--app-border))] px-4 py-2 text-sm font-semibold"
                >
                  CSV
                </button>
              </div>
            </div>

            <div className="overflow-hidden rounded-[24px] border border-[rgb(var(--app-border)/0.72)]">
              <table className="min-w-full text-sm">
                <thead className="bg-[rgb(var(--app-surface-muted)/0.72)]">
                  <tr>
                    {(
                      [
                        ['full_name', 'Name'],
                        ['phone', 'Phone'],
                      ] as const
                    ).map(([key, label]) => (
                      <th
                        key={key}
                        className="px-4 py-3 text-left font-semibold"
                      >
                        <button
                          type="button"
                          aria-label={`Sort by ${label}`}
                          onClick={() => {
                            setSort((current) => toggleSortState(current, key));
                            setPage(1);
                          }}
                        >
                          {label}
                          {sort.key === key
                            ? sort.direction === 'asc'
                              ? ' ↑'
                              : ' ↓'
                            : ''}
                        </button>
                      </th>
                    ))}
                    <th className="px-4 py-3 text-right font-semibold"> </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td className="px-4 py-6 text-gray-500" colSpan={3}>
                        Loading…
                      </td>
                    </tr>
                  ) : contacts.length === 0 ? (
                    <tr>
                      <td className="px-4 py-6 text-gray-500" colSpan={3}>
                        No VIP contacts for this {scope}.
                      </td>
                    </tr>
                  ) : (
                    contacts.map((contact) => (
                      <tr
                        key={contact.id}
                        className="border-t border-[rgb(var(--app-border)/0.5)]"
                      >
                        <td className="px-4 py-3">
                          {contact.display_name || '—'}
                        </td>
                        <td className="px-4 py-3">{contact.phone}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            className="text-sm text-error-600"
                            onClick={() => handleDelete(contact.id)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>
                {count} contact{count === 1 ? '' : 's'}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="rounded-xl border px-3 py-1 disabled:opacity-40"
                >
                  Previous
                </button>
                <span>
                  Page {page} of {totalPages}
                </span>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="rounded-xl border px-3 py-1 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <VipCsvImportModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => {
          setPage(1);
          loadContacts();
        }}
        scope={scope}
        storeId={selectedStoreId}
        storeGroupId={selectedGroupId}
        requestOptions={requestOptions}
      />
    </ComponentCard>
  );
};

export default VipContactsTab;
